import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home, PhoneCall, LifeBuoy, Mail } from 'lucide-react';
import { logger } from '../utils/logger';
import { getErrorSurfaceBranding } from '../utils/errorSurfaceBranding';
import {
  alreadyTriedStaleBundleReload,
  CHUNK_RELOAD_GUARD_KEY,
  clearStaleBundleGuards,
  isChunkLoadError,
  isRealtimeSubscribeOrderError,
  isStaleModuleReferenceError,
  reloadFreshApp,
} from '../utils/chunkLoadError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * ErrorBoundary Component
 *
 * Catches React errors in child components and displays a user-friendly error UI
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public componentDidMount() {
    // Once the app has been alive and healthy for a few seconds, clear the
    // one-time chunk-reload guard so a *future* deploy can self-heal again.
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if (!this.state.hasError) {
        clearStaleBundleGuards();
      }
    }, 5000);

    // Vite HMR can leave this boundary stuck after a partial hot update.
    if (import.meta.env.DEV && import.meta.hot) {
      const clearStaleBoundary = () => {
        if (this.state.hasError) {
          this.setState({ hasError: false, error: undefined, errorInfo: undefined });
        }
      };
      import.meta.hot.on('vite:beforeUpdate', clearStaleBoundary);
      import.meta.hot.on('vite:afterUpdate', clearStaleBoundary);
    }
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Self-heal stale-chunk / CSS-preload errors: a one-time hard reload pulls fresh
    // bundles. Guarded by sessionStorage so we never loop if the asset is truly missing.
    const shouldSelfHeal =
      (isChunkLoadError(error) && !import.meta.env.DEV) ||
      (isRealtimeSubscribeOrderError(error) && !import.meta.env.DEV) ||
      (isStaleModuleReferenceError(error) && typeof window !== 'undefined');
    if (shouldSelfHeal && typeof window !== 'undefined' && !alreadyTriedStaleBundleReload()) {
      try {
        window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, 'true');
      } catch {
        /* ignore */
      }
      logger.warn('ErrorBoundary: stale chunk detected, reloading for fresh bundles', {
        error: error.message,
        url: window.location.href,
      });
      const fresh = new URL(window.location.href);
      fresh.searchParams.set('_fresh', String(Date.now()));
      window.location.replace(fresh.toString());
      return;
    }

    const isHooksError = error.message.includes('310') || error.message.includes('Rendered more hooks');

    logger.error('ErrorBoundary caught error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      isHooksError,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    if (import.meta.env.DEV) {
      console.error('🔴 ErrorBoundary - Full Error Details:', {
        error,
        errorInfo,
        componentStack: errorInfo.componentStack,
        currentPath: window.location.pathname,
        searchParams: window.location.search,
      });
    }

    this.setState({ errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    this.props.onReset?.();
  };

  private handleReload = () => {
    reloadFreshApp();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return (
          <div className="space-y-2">
            {this.props.fallback}
            {this.state.error?.message ? (
              <p className="break-words text-xs text-rose-800">{this.state.error.message}</p>
            ) : null}
          </div>
        );
      }

      let branding;
      try {
        branding = getErrorSurfaceBranding();
      } catch {
        branding = {
          productName: 'ServiceApp',
          headline: 'Something went wrong',
          supportEmail: 'hello@serviceapp.ai',
          supportPhone: null,
          displayPhone: null,
          supportTicketUrl: '/support',
          logoUrl: null,
          variant: 'saas-platform' as const,
        };
      }
      const isSaas = branding.variant === 'saas-platform';

      return (
        <div
          className={`min-h-screen flex items-center justify-center p-4 ${
            isSaas ? 'bg-slate-950' : 'bg-slate-950'
          }`}
        >
          <div
            className={`max-w-3xl w-full backdrop-blur-lg border rounded-3xl overflow-hidden ${
              isSaas
                ? 'bg-slate-900/80 border-white/10 shadow-[0_20px_60px_rgba(99,102,241,0.25)]'
                : 'bg-white/10 border-white/10 shadow-[0_20px_60px_rgba(15,23,42,0.6)]'
            }`}
          >
            <div
              className={`p-6 sm:p-8 ${
                isSaas
                  ? 'bg-gradient-to-r from-indigo-600 via-violet-600 to-cyan-500'
                  : 'bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-500'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div className="flex items-center gap-4">
                  <span
                    className={`h-14 w-14 rounded-2xl flex items-center justify-center ${
                      isSaas ? 'bg-white/15' : 'bg-white/20 text-slate-900'
                    }`}
                  >
                    {branding.logoUrl ? (
                      <img
                        src={branding.logoUrl}
                        alt=""
                        className="h-10 w-10 rounded-lg object-contain"
                        width={40}
                        height={40}
                      />
                    ) : (
                      <LifeBuoy className="h-8 w-8" />
                    )}
                  </span>
                  <div>
                    <p
                      className={`text-xs uppercase tracking-[0.3em] ${
                        isSaas ? 'text-white/80' : 'text-white/80'
                      }`}
                    >
                      {branding.productName}
                    </p>
                    <h1
                      className={`text-2xl sm:text-3xl font-bold leading-tight ${
                        isSaas ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      {branding.headline}
                    </h1>
                  </div>
                </div>
                {branding.supportPhone && branding.displayPhone && !isSaas && (
                  <div className="hidden sm:flex flex-col items-end text-slate-900">
                    <span className="text-xs uppercase tracking-widest text-slate-900/70">
                      Emergency Support
                    </span>
                    <a
                      href={`tel:${branding.supportPhone}`}
                      className="text-xl font-bold tracking-wide hover:text-slate-900/80"
                    >
                      {branding.displayPhone}
                    </a>
                  </div>
                )}
                {isSaas && (
                  <div className="hidden sm:flex flex-col items-end text-white">
                    <span className="text-xs uppercase tracking-widest text-white/70">Platform support</span>
                    <a
                      href={`mailto:${branding.supportEmail}`}
                      className="text-lg font-bold tracking-wide hover:text-white/80"
                    >
                      {branding.supportEmail}
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 sm:p-10 bg-slate-900 text-slate-100">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold leading-tight">
                        {isChunkLoadError(this.state.error)
                          ? 'A newer version of the app is available'
                          : 'Something unexpected happened'}
                      </h2>
                      <p className="text-slate-300 leading-relaxed">
                        {isChunkLoadError(this.state.error)
                          ? 'Use Refresh app to load the latest update. Your data is safe.'
                          : isSaas
                            ? 'Your workspace data is safe. Try again or email our team — we typically respond within one business day.'
                            : 'Your data remains safe and our team has been notified. If you were in the middle of an important task, you can try again or reach our support crew immediately.'}
                      </p>
                    </div>
                  </div>

                  {this.state.error?.message ? (
                    <div className="bg-slate-900/60 border border-slate-700/80 rounded-xl p-4">
                      <p className="text-xs font-mono text-slate-300 break-words">
                        {this.state.error.message}
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="w-full lg:w-72 bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm uppercase tracking-wide text-white/50">Get help</p>
                    {branding.supportPhone && branding.displayPhone && !isSaas ? (
                      <a
                        href={`tel:${branding.supportPhone}`}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-yellow-400 text-slate-900 font-semibold shadow-lg hover:bg-yellow-300 transition"
                      >
                        <PhoneCall className="h-4 w-4" />
                        Call {branding.displayPhone}
                      </a>
                    ) : (
                      <a
                        href={`mailto:${branding.supportEmail}`}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold shadow-lg transition ${
                          isSaas
                            ? 'bg-gradient-to-r from-indigo-500 to-cyan-500 text-white hover:brightness-110'
                            : 'bg-yellow-400 text-slate-900 hover:bg-yellow-300'
                        }`}
                      >
                        <Mail className="h-4 w-4" />
                        Email {branding.supportEmail}
                      </a>
                    )}
                    {isSaas && branding.supportTicketUrl && (
                      <a
                        href={branding.supportTicketUrl}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-cyan-400/50 text-cyan-200 hover:bg-cyan-400/10 font-semibold transition"
                      >
                        <LifeBuoy className="h-4 w-4" />
                        Open a support ticket
                      </a>
                    )}
                  </div>
                  {branding.supportPhone && branding.displayPhone && !isSaas && (
                    <p className="text-xs text-slate-400">
                      Prefer email? Reach us at{' '}
                      <a
                        href={`mailto:${branding.supportEmail}`}
                        className="text-yellow-300 hover:text-yellow-200 font-medium"
                      >
                        {branding.supportEmail}
                      </a>
                      .
                    </p>
                  )}
                  {isSaas && branding.supportPhone && branding.displayPhone && (
                    <p className="text-xs text-slate-400">
                      Platform phone (Super Admin):{' '}
                      <a
                        href={`tel:${branding.supportPhone}`}
                        className="text-cyan-300 hover:text-cyan-200 font-medium"
                      >
                        {branding.displayPhone}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <button
                  onClick={() => {
                    reloadFreshApp();
                  }}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition ${
                    isSaas
                      ? 'bg-gradient-to-r from-indigo-500 to-cyan-500 text-white hover:brightness-110'
                      : 'bg-blue-500 hover:bg-blue-400 text-white'
                  }`}
                >
                  <RefreshCw className="h-4 w-4" />
                  {isChunkLoadError(this.state.error) ? 'Refresh app' : 'Try Again'}
                </button>

                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold transition"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload Page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold transition"
                >
                  <Home className="h-4 w-4" />
                  Back to Home
                </button>

                {isSaas && branding.supportTicketUrl ? (
                  <a
                    href={branding.supportTicketUrl}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 font-semibold transition"
                  >
                    <LifeBuoy className="h-4 w-4" />
                    Support ticket
                  </a>
                ) : branding.supportPhone && !isSaas ? (
                  <a
                    href={`tel:${branding.supportPhone}`}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-yellow-400 text-yellow-300 hover:bg-yellow-400/10 font-semibold transition"
                  >
                    <PhoneCall className="h-4 w-4" />
                    Emergency Support
                  </a>
                ) : (
                  <a
                    href={`mailto:${branding.supportEmail}`}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 font-semibold transition"
                  >
                    <Mail className="h-4 w-4" />
                    Contact Support
                  </a>
                )}
              </div>

              {import.meta.env.DEV && this.state.errorInfo && (
                <details className="mt-8 text-xs bg-slate-950/40 border border-slate-800 rounded-xl overflow-hidden">
                  <summary className="cursor-pointer px-4 py-3 text-slate-400 hover:text-slate-200">
                    Technical details (dev only)
                  </summary>
                  <pre className="mt-0 p-4 bg-slate-950 text-slate-300 overflow-x-auto text-[11px] leading-relaxed">
                    {this.state.error?.stack}
                    {'\n\n'}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
